import { createSelector } from '@reduxjs/toolkit';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import api from '@/redux/api/itemDetail';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_ITEM_DETAILS_FAIL = 'la/domain/itemDetail/LOAD_FAIL';
export const LOAD_ITEM_DETAILS_REQUEST = 'la/domain/itemDetail/LOAD_REQUEST';
export const LOAD_ITEM_DETAILS_SUCCESS = 'la/domain/itemDetail/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
export const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_ITEM_DETAILS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.itemIds),
            };
        case LOAD_ITEM_DETAILS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_ITEM_DETAILS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.itemDetails) {
                action.payload.itemDetails.forEach((item) => {
                    existing[item.itemId] = { ...item };
                    loaded[item.itemId] = time;
                    loading = difference(loading, [item.itemId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.itemDetail;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

// takes in a (state, id) and passes both params to every function on
// the way down.
export const getItemDetail = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getHasRequiredShippingDimensions = createSelector(getItemDetail, (itemDetail) => {
    const { shippingDimensions = '' } = itemDetail || {};
    const parts = shippingDimensions.split(' ').filter((i) => i !== 'x');
    // we are checking to see that they have at least 3 parts and that the last part is a "unit"
    return parts.length >= 3 && isNaN(Number(parts[parts.length - 1]));
});

const getLoadTimeForItemDetail = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isItemDetailLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchItemDetail = (state, itemId) => {
    if (!itemId) {
        return false;
    }
    const item = getItemDetail(state, itemId);
    if (item) {
        const loaded = getLoadTimeForItemDetail(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isItemDetailLoading(state, itemId);
    return !loading;
};

const whichItemDetailsNeeded = (state, itemIds) => itemIds.filter((itemId) => shouldFetchItemDetail(state, itemId));

/* ACTION CREATORS */
const loadItemDetails = (itemIds) => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);

        dispatch({
            payload: itemIds,
            type: LOAD_ITEM_DETAILS_REQUEST,
        });

        const response = await api.fetchItemDetailsByIds({ deployment, itemIds });

        dispatch({
            meta: { actionTime: Date.now(), itemIds },
            payload: response.data,
            type: LOAD_ITEM_DETAILS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemIds },
            payload: error,
            type: LOAD_ITEM_DETAILS_FAIL,
        });
    }
};

export const fetchItemDetailsIfNeeded = (itemIds: number[]) => async (dispatch: Function, getState: Function) => {
    const needed = whichItemDetailsNeeded(getState(), itemIds);
    if (needed.length) {
        return dispatch(loadItemDetails(needed));
    }
    return Promise.resolve();
};
